import './Button.css'

import { clsx } from 'clsx'
import type {
  AnchorHTMLAttributes,
  AnimationEvent,
  ButtonHTMLAttributes,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
} from 'react'
import { useState } from 'react'
import type { NavLinkProps } from 'react-router'
import { NavLink } from 'react-router'

import { Tooltip } from '#design-system/component/Tooltip/Tooltip'
import type { IconElement } from '#design-system/icons/factory'

export type ActionRole = 'primary' | 'secondary' | 'ghost' | 'destruction'

/** Keycap footprint. `standard` aligns with form-element rows; `small` is the compact variant. */
export type ButtonSize = 'standard' | 'small'

/** Name of the success/error flash @keyframes in design-system/loading.css. The animationend
 *  handler keys on this to reset flash state; Button.test.tsx asserts the CSS declaration stays in sync. */
export const LOADING_FLASH_ANIMATION = 'doss-loading-flash'

/**
 * onClick may return either void (sync) or a Promise (async). React's built-in MouseEventHandler
 * is `(e) => void` because React itself never awaits handlers — but that means async work in
 * onClick can leave its UI affordance behind. We widen explicitly so the Button can detect a
 * thenable return at runtime and manage its own loading state.
 *
 * Expressed as a union of function types (rather than `() => void | Promise<unknown>`) to keep
 * `void` in a sole-return-type position — @typescript-eslint/no-invalid-void-type rejects `void`
 * inside a union return type, but allows it as the entire return type of a function.
 */
type ButtonClickHandler =
  | ((event: MouseEvent<HTMLButtonElement>) => void)
  | ((event: MouseEvent<HTMLButtonElement>) => Promise<unknown>)

interface BaseButtonProps {
  actionRole: ActionRole
  /** If provided, the button handlers/behavior are disabled and the message is surfaced via a tooltip */
  disabledMessage?: string | undefined
  /** Keycap footprint. Defaults to `standard`. */
  size?: ButtonSize | undefined
  className?: string | undefined
}

/** Visible label provides the accessible name. Icon is optional. */
export interface LabeledProps {
  label: string
  icon?: IconElement
}

/** Icon-only — caller MUST supply `aria-label` so the element has an accessible name.
 * `label?: never` keeps the union discriminated. */
export interface IconOnlyProps {
  icon: IconElement
  label?: never
  'aria-label': string
}

/** Interactive elements must have a label and/or icon, but never neither.
 *  Shared across the action family (Button, ToggleGroup items, etc). */
export type LabelOrIconProps = LabeledProps | IconOnlyProps

/** Buttons must have a label and/or icon, but never neither */
export type ButtonLabelProps =
  | LabeledProps
  | (IconOnlyProps & {
      /* Icon-only btns cannot be primary: an icon isn't prominent enough for the most important action in a context */
      actionRole: Exclude<ActionRole, 'primary'>
    })

interface ButtonTagProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'disabled' | 'onClick'
> {
  tagType?: 'button'
  onClick?: ButtonClickHandler
  ref?: Ref<HTMLButtonElement>
  href?: never
  to?: never
}

interface AnchorTagProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'className' | 'href'
> {
  tagType: 'a'
  href: string
  ref?: Ref<HTMLAnchorElement>
  to?: never
}

interface NavLinkTagProps extends Omit<NavLinkProps, 'className' | 'to'> {
  tagType: 'NavLink'
  /** Per the design doc, links navigate or destroy — they never carry primary or secondary
   * action chrome. */
  actionRole: 'ghost' | 'destruction'
  to: NavLinkProps['to']
  href?: never
}

export type ButtonProps = BaseButtonProps &
  ButtonLabelProps &
  (ButtonTagProps | AnchorTagProps | NavLinkTagProps)

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  value !== null && value !== undefined && typeof (value as { then?: unknown }).then === 'function'

export const Button = (props: ButtonProps): ReactElement => {
  const { actionRole, className, disabledMessage, icon, label, size = 'standard' } = props

  const [isLoading, setIsLoading] = useState(false)
  const [hasErrored, setHasErrored] = useState(false)
  const [hasSucceeded, setHasSucceeded] = useState(false)

  const isDisabled = !!disabledMessage || isLoading

  const body: ReactNode = (
    <>
      {icon ?? null}
      {label !== undefined ? <span className="label">{label}</span> : null}
    </>
  )

  const classNames = clsx(
    'doss-button',
    'doss-loadable',
    actionRole,
    size === 'small' && 'small',
    isDisabled && 'disabled',
    isLoading && 'loading',
    hasErrored && 'error',
    hasSucceeded && 'success',
    className,
  )

  const wrapInTooltip = (trigger: ReactElement): ReactElement =>
    disabledMessage !== undefined ? <Tooltip title={disabledMessage}>{trigger}</Tooltip> : trigger

  if (props.tagType === 'a') {
    const {
      actionRole,
      className,
      disabledMessage,
      icon,
      label,
      size,
      tagType,
      onClick,
      ...anchorRest
    } = props
    const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>): void => {
      if (isDisabled) {
        event.preventDefault()
        return
      }
      onClick?.(event)
    }
    return wrapInTooltip(
      <a
        {...anchorRest}
        aria-disabled={isDisabled || undefined}
        className={classNames}
        onClick={handleAnchorClick}
      >
        {body}
      </a>,
    )
  }

  if (props.tagType === 'NavLink') {
    const {
      actionRole,
      className,
      disabledMessage,
      icon,
      label,
      size,
      tagType,
      onClick,
      ...navRest
    } = props
    const handleNavClick = (event: MouseEvent<HTMLAnchorElement>): void => {
      if (isDisabled) {
        event.preventDefault()
        return
      }
      onClick?.(event)
    }
    return wrapInTooltip(
      <NavLink
        {...navRest}
        aria-disabled={isDisabled || undefined}
        className={({ isActive }) => clsx(classNames, isActive && 'active')}
        onClick={handleNavClick}
      >
        {body}
      </NavLink>,
    )
  }

  const {
    actionRole: _actionRole,
    className: _className,
    disabledMessage: _disabledMessage,
    icon: _icon,
    label: _label,
    size: _size,
    onClick,
    tagType,
    type = 'button',
    ...btnRest
  } = props

  // Wrap consumer's onClick. If it returns a thenable, flip into a loading state for the duration
  // of the promise. We call the consumer's handler directly (no await) so synchronous work runs
  // synchronously and React's batching is preserved.
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (isDisabled) {
      event.preventDefault()
      return
    }
    if (!onClick) {
      return
    }
    const result = onClick(event)
    if (isThenable(result)) {
      // A new click clears any lingering flashes so the loading orbit isn't visually
      // overridden by a still-pending .error / .success class from the previous attempt.
      setHasErrored(false)
      setHasSucceeded(false)
      setIsLoading(true)
      // The void operator marks the chained promise as intentionally discarded — the Button only
      // manages its own loading visual. Surfacing async errors is the consumer's responsibility
      // (they can attach their own .catch on the original onClick body).
      void Promise.resolve(result).then(
        () => {
          setIsLoading(false)
          setHasSucceeded(true)
        },
        () => {
          setIsLoading(false)
          setHasErrored(true)
        },
      )
    }
  }

  // Reset state once css animation has completed so we can be ready to start the next one.
  const handleAnimationEnd = (event: AnimationEvent<HTMLButtonElement>): void => {
    btnRest.onAnimationEnd?.(event)
    if (event.animationName === LOADING_FLASH_ANIMATION) {
      setHasSucceeded(false)
      setHasErrored(false)
    }
  }

  return wrapInTooltip(
    <button
      {...btnRest}
      aria-busy={isLoading || undefined}
      aria-disabled={isDisabled || undefined}
      className={classNames}
      onAnimationEnd={handleAnimationEnd}
      onClick={handleClick}
      type={type}
    >
      {body}
    </button>,
  )
}
